import React, { useState, useEffect, useMemo } from 'react'
import { Row, Col, Card, Statistic, Space, Spin, Button } from 'antd'
import { HomeOutlined, EnvironmentOutlined, FileDoneOutlined } from '@ant-design/icons'
import InteractiveMap from './InteractiveMap'
import apiClient from '@app/apiClient'
import { useReviewerActions } from '@app/pages/reviewer/hooks/useReviewerActions'
import './SubmissionSummary.scss'


// Custom hook to fetch appraisal XML data for property details
const useAppraisalData = (orderId, submissionVersion) => {
  const [appraisalData, setAppraisalData] = useState(null)
  const [loading, setLoading] = useState(false)

  const parsedSubmissionFilesApi = useMemo(() => apiClient(SERVICE_URL), [])

  useEffect(() => {
    if (!orderId || !submissionVersion) {
      return
    }

    const fetchAppraisalData = async () => {
      try {
        setLoading(true)

        // Fetch XML parsing results for appraisal basic data
        const parsedResults = await parsedSubmissionFilesApi.get('parsed-submission-files/results/v2', {
          order_id: orderId,
          submission_version: submissionVersion,
          file_type: 'appraisal_xml',
          solution: 'appraisal_basic',
        })

        if (!parsedResults?.results?.[0]?._id) {
          console.warn('No XML parsing results found')
          return
        }

        // Get the full parsing result with all property data
        const completeResult = await parsedSubmissionFilesApi.get(
          `parsed-submission-files/results/v2/${parsedResults.results[0]._id}`
        )

        if (completeResult?.result) {
          setAppraisalData(completeResult.result)
        }
      } catch (error) {
        console.error('Error fetching appraisal data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchAppraisalData()
  }, [orderId, submissionVersion, parsedSubmissionFilesApi])

  return { appraisalData, loading }
}

// Custom hook to fetch RestB results and find front house image
const useFrontHouseImage = (orderId, submissionVersion) => {
  const [frontHouseImageUrl, setFrontHouseImageUrl] = useState(null)
  const [loading, setLoading] = useState(false)

  const submissionReviewsApi = useMemo(() => apiClient(SERVICE_URL), [])
  const parsedSubmissionFilesApi = useMemo(() => apiClient(SERVICE_URL), [])

  useEffect(() => {
    if (!orderId || !submissionVersion) {
      return
    }

    const fetchFrontHouseImage = async () => {
      try {
        setLoading(true)

        // Step 1: Fetch PDF parsing results to get images
        const parsedResults = await parsedSubmissionFilesApi.get('parsed-submission-files/results/v2', {
          order_id: orderId,
          submission_version: submissionVersion,
          file_type: 'appraisal_pdf',
          solution: 'appraisal_warranty',
        })

        if (!parsedResults?.results?.[0]?._id) {
          console.warn('No PDF parsing results found')
          return
        }

        // Get the full parsing result with images
        const completeResult = await parsedSubmissionFilesApi.get(
          `parsed-submission-files/results/v2/${parsedResults.results[0]._id}`,
          { presign: true }
        )

        if (!completeResult?.result?.images) {
          console.warn('No images found in parsing result')
          return
        }

        // Step 2: Fetch RestB results to get roomtype analysis
        const restbResults = await submissionReviewsApi.get('submission-reviews/restb-results/v1', {
          order_id: orderId,
          submission_version: submissionVersion,
        })

        if (!restbResults?.results?.[0]?.solutions?.roomtype?.results) {
          console.warn('No RestB roomtype results found')
          return
        }

        // Step 3: Process the data to find front_house image
        const images = completeResult.result.images
        const roomtypeResults = restbResults.results[0].solutions.roomtype.results

        // Create mapping of subject images (same logic as ImageAnalysis)
        const subjectImageIndices = []
        images.forEach((imageData, index) => {
          if (imageData.category === 'subject' || !imageData.category) {
            subjectImageIndices.push(index)
          }
        })

        // Find the image with front_house prediction
        for (let solutionIndex = 0; solutionIndex < roomtypeResults.length; solutionIndex++) {
          const result = roomtypeResults[solutionIndex]
          if (result.values?.top_prediction?.label === 'front_house' ||
            result.values?.predictions?.some(p => p.label === 'front_house')
          ) {
            const imageIndex = subjectImageIndices[solutionIndex]
            if (imageIndex !== undefined && imageIndex < images.length) {
              const frontHouseImage = images[imageIndex]
              if (frontHouseImage?.image_url) {
                setFrontHouseImageUrl(frontHouseImage.image_url)
                break
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching front house image:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchFrontHouseImage()
  }, [orderId, submissionVersion, submissionReviewsApi, parsedSubmissionFilesApi])

  return { frontHouseImageUrl, loading }
}

const SubmissionSummaryContent = ({ submission, orderData, loanData, comps }) => {
  // Use the custom hooks to fetch data
  const { frontHouseImageUrl, loading: frontHouseLoading } = useFrontHouseImage(
    orderData?.id,
    submission?.version
  )

  const { appraisalData, loading: appraisalLoading } = useAppraisalData(
    orderData?.id,
    submission?.version
  )

  // Hook for navigating to reviewer
  const { navigateToReviewerFromOrderId } = useReviewerActions()

  // Handler for Review PDF button
  const onNavigateToReviewer = () => {
    navigateToReviewerFromOrderId(orderData.id, submission.version)
  }

  if (!submission) {
    return (
      <div className="submission-summary-container">
        <div className="mr-auto">
          <p>No Submission data to display.</p>
        </div>
      </div>
    )
  }

  // --- Helper Functions ---

  // Helper to get value from appraisal data
  const getAppraisalValue = (field, defaultValue = 'N/A') => {
    if (!appraisalData) { return defaultValue }
    const value = appraisalData[field]
    return (value !== undefined && value !== null && value !== '') ? value : defaultValue
  }

  const formatCurrency = (value) => {
    const num = parseFloat(value)
    if (isNaN(num)) {
      return 'N/A'
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(num)
  }

  // --- Render Functions ---

  const renderPropertyOverviewCard = () => (
    <Card className="summary-card-base property-overview-card" style={{ position: 'relative' }}>
      <div className="card-main-content">
        <div className="photo-section">
          {frontHouseLoading ? (
            <div className="photo-placeholder loading">
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>Loading Front of House Photo...</div>
            </div>
          ) : frontHouseImageUrl ? (
            <div className="front-house-photo">
              <img
                src={frontHouseImageUrl}
                alt="Front of House"
                className="front-house-photo-img"
              />
            </div>
          ) : (
            <div className="photo-placeholder">
              <HomeOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <div>Front of House Photo</div>
              <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                No front house image detected
              </div>
            </div>
          )}
        </div>
        <div className="details-section">
          {appraisalLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <Spin size="large" />
              <span style={{ marginLeft: '16px' }}>Loading property details...</span>
            </div>
          ) : (
            <div className="details-grid">
              <div className="detail-item">
                <div className="detail-label">Address</div>
                <div className="detail-value">{subjectAddress || '-'}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Beds</div>
                <div className="detail-value">{getAppraisalValue('subject_property_total_bedroom_count')}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Bathrooms</div>
                <div className="detail-value">{getAppraisalValue('subject_property_total_bathroom_count')}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Lot Size</div>
                <div className="detail-value">{getAppraisalValue('site_area_description')}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">GLA</div>
                <div className="detail-value">{getAppraisalValue('gross_living_area_square_feet_count') ? `${getAppraisalValue('gross_living_area_square_feet_count')} sf` : '-'}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Year Built</div>
                <div className="detail-value">{getAppraisalValue('property_structure_built_year')}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Condition</div>
                <div className="detail-value">{getAppraisalValue('gse_overall_condition_type_subject')}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Quality</div>
                <div className="detail-value">{getAppraisalValue('gse_quality_of_construction_rating_type_subject')}</div>
              </div>
            </div>
          )}
        </div>
        <div className="review-pdf-button-container" style={{
          position: 'absolute',
          bottom: '16px',
          right: '16px',
        }}>
          <Button
            type="primary"
            icon={<FileDoneOutlined />}
            onClick={onNavigateToReviewer}
          >
            Review PDF
          </Button>
        </div>
      </div>
    </Card>
  )

  const renderValuesRow = () => (
    <Row gutter={[24, 24]} className="values-row">
      <Col xs={24} sm={12} md={8} lg={6}>
        <Card className="value-statistic-card">
          {appraisalLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '120px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              <Statistic
                title="As Is Value:"
                value={formatCurrency(getAppraisalValue('property_appraised_value_amount'))}
              />
              {getAppraisalValue('property_appraised_value_amount_as_repaired', null) && <Statistic
                title="As Repaired Value:"
                value={formatCurrency(getAppraisalValue('property_appraised_value_amount_as_repaired'))}
              />}
            </>
          )}
        </Card>
      </Col>
      <Col xs={24} sm={24} md={16} lg={18}>
        <Card title="Reconciliation Commentary" className="reconciliation-card">
          {appraisalLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '120px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <p>
              {getAppraisalValue('valuation_reconciliation_summary_comment')}
            </p>
          )}
        </Card>
      </Col>
    </Row>
  )

  // Get subject address from appraisal XML data only
  const getSubjectAddress = () => {
    if (!appraisalData) { return null }

    // Try sales_comparables[0] first (most accurate)
    if (appraisalData.sales_comparables?.[0]?.sales_section_property_street_address) {
      const subject = appraisalData.sales_comparables[0]
      return `${subject.sales_section_property_street_address}${subject.sales_section_property_city?.[0] ? `, ${subject.sales_section_property_city[0]}` : ''}`
    }

    // Fallback to property fields from XML
    const street = getAppraisalValue('property_street_address', '')
    const city = getAppraisalValue('property_city', '')
    const state = getAppraisalValue('property_state', '')
    const zip = getAppraisalValue('property_postal_code', '')

    if (street) {
      return `${street}${city ? `, ${city}` : ''}${state ? `, ${state}` : ''}${zip ? ` ${zip}` : ''}`.trim()
    }

    return null
  }

  // Extract sales comparables from appraisal data (excluding subject)
  const getComparablesFromAppraisal = () => {
    if (!appraisalData?.sales_comparables) { return null }

    const validComps = appraisalData.sales_comparables
      .filter(comp => comp.sales_section_property_street_address)

    if (validComps.length <= 1) { return null } // Need at least subject + 1 comp

    // Skip the first one (subject) and return the rest as comparables
    return validComps.slice(1).map((comp, index) => ({
      fullAddressCityStateZip: `${comp.sales_section_property_street_address}${comp.sales_section_property_city?.[0] ? `, ${comp.sales_section_property_city[0]}` : ''}`,
      number: index + 1,
      salesPrice: comp.property_sales_amount,
      adjustedPrice: comp.adjusted_sales_price_amount,
      proximityToSubject: comp.proximity_to_subject_description,
    }))
  }

  // Get data from appraisal XML only
  const subjectAddress = getSubjectAddress()
  const comparables = getComparablesFromAppraisal() || comps || []

  return (
    <div className="submission-tab-content submission-summary-container">
      {renderPropertyOverviewCard()}
      {renderValuesRow()}

      <div className="comparables-section">
        <Card
          title={
            <Space>
              <EnvironmentOutlined />
              <span>Comparables Map</span>
            </Space>
          }
          className="section-card"
        >
          <InteractiveMap
            subject={subjectAddress}
            comps={comparables}
          />
        </Card>
      </div>
    </div>
  )
}

export default SubmissionSummaryContent
